import { c as _c } from "react/compiler-runtime";
import { Box, Stack, type SxProps, Typography, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import { ArgumentType } from "#/entities/argument/model/argumentObject";
import { type ArrayData, arrayDataItemSelectors } from "#/entities/dataStructures/array/model/arraySlice";
import { ArrayItem } from "./ArrayItem";
import { StructureDisplayLabel } from "./StructureDisplayLabel";
type ArrayBracketProps = {
  argType: ArgumentType;
  side?: "left" | "right";
};
const ArrayBracket: React.FC<ArrayBracketProps> = t0 => {
  const $ = _c(9);
  const {
    argType,
    side: t1
  } = t0;
  const side = t1 === undefined ? "left" : t1;
  const theme = useTheme();
  if (argType === ArgumentType.SET) {
    const char = side === "left" ? "<" : ">";
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        color: "primary.light",
        fontSize: 26,
        fontWeight: 200,
        display: "inline-block",
        height: 37,
        opacity: 0.6,
        transform: "scale(0.8, 2)"
      };
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    let t3;
    if ($[1] !== char) {
      t3 = <Typography component="span" className="array-bracket" sx={t2}>{char}</Typography>;
      $[1] = char;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    return t3;
  }
  if (argType === ArgumentType.STRING) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Typography>"</Typography>;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  const borderStyle = `2px solid ${theme.palette.primary.light}`;
  const t2 = side === "left" ? "-12px" : 0;
  const t3 = side === "left" ? 0 : "-12px";
  const t4 = side === "left" ? "none" : "rotate(180deg)";
  let t5;
  if ($[4] !== borderStyle || $[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = <Box className="array-bracket" sx={{
      width: 12,
      height: 42,
      marginRight: t2,
      marginLeft: t3,
      borderRadius: "2px 0 0 2px",
      borderLeft: borderStyle,
      borderTop: borderStyle,
      borderBottom: borderStyle,
      opacity: 0.6,
      transition: "opacity 0.1s",
      zIndex: 10,
      pointerEvents: "none",
      transform: t4
    }} />;
    $[4] = borderStyle;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
export type ArrayStructureViewProps = {
  data: ArrayData;
  parentColorMap?: ArrayData["colorMap"];
  sx?: SxProps;
};
export const ArrayStructureView: React.FC<ArrayStructureViewProps> = t0 => {
  const $ = _c(23);
  const {
    data,
    parentColorMap
  } = t0;
  let t1;
  if ($[0] !== data.nodes) {
    t1 = arrayDataItemSelectors.selectAll(data.nodes);
    $[0] = data.nodes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const items = t1;
  const {
    argType,
    displayLabel
  } = data;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      width: "fit-content",
      zIndex: 10
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== displayLabel) {
    t3 = displayLabel ? <StructureDisplayLabel label={displayLabel} /> : null;
    $[3] = displayLabel;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      width: "fit-content",
      "&:hover": {
        ".array-bracket": {
          opacity: 1
        },
        ".array-item": {
          "&::before": {
            opacity: 1
          }
        }
      }
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== argType) {
    t5 = <ArrayBracket argType={argType} />;
    $[6] = argType;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== data.colorMap || $[9] !== items || $[10] !== parentColorMap) {
    t6 = items.length > 0 ? items.map(item => <ArrayItem key={item.id} colorMap={data.colorMap ?? parentColorMap} item={item} />) : <Box sx={{
      width: 32,
      height: 12
    }} />;
    $[8] = data.colorMap;
    $[9] = items;
    $[10] = parentColorMap;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <Stack direction="row">{t6}</Stack>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== argType) {
    t8 = <ArrayBracket argType={argType} side="right" />;
    $[14] = argType;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t5 || $[17] !== t7 || $[18] !== t8) {
    t9 = <Stack direction="row" sx={t4}>{t5}{t7}{t8}</Stack>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t3 || $[21] !== t9) {
    t10 = <Stack direction="column" sx={t2}>{t3}{t9}</Stack>;
    $[20] = t3;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
};